import { useEffect, useReducer, useRef, useCallback } from 'react';
import { FetchDataError } from '../models/fetch-data';

interface FetcherState<T> {
  isLoading: boolean;
  error: FetchDataError;
  data: T;
}

type Action<T> =
  | { type: 'FETCH_INIT' }
  | { type: 'FETCH_SUCCESS'; payload: T }
  | { type: 'FETCH_FAILURE'; payload: FetchDataError };

type FetcherFn<T> = () => Promise<T>;

function dataFetchReducer<T>(state: FetcherState<T>, action: Action<T>): FetcherState<T> {
  switch (action.type) {
    case 'FETCH_INIT':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        isLoading: false,
        error: null,
        data: action.payload,
      };
    case 'FETCH_FAILURE':
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    default:
      throw new Error();
  }
}

export function useDataFetcher<T>(
  initialData?: T,
  options: {
    onSuccess?: (data: T) => void;
    onError?: (error: Error) => void;
  } = {},
): [FetcherState<T>, (fn: FetcherFn<T>) => any] {
  const { onSuccess, onError } = options;
  const [state, dispatch] = useReducer(dataFetchReducer, {
    isLoading: false,
    error: null,
    data: initialData,
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current?.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(initialData) && initialData.length === 0) {
      return;
    }
    dispatch({ type: 'FETCH_SUCCESS', payload: initialData });
  }, [initialData]);

  const executeFetch = useCallback(
    async (fetcher: FetcherFn<T>) => {
      // Clean up previous fetch
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this fetch
      abortControllerRef.current = new AbortController();
      const { signal } = abortControllerRef.current;

      dispatch({ type: 'FETCH_INIT' });

      try {
        // Race between the fetch and the abort signal
        const promise = fetcher();
        const response = await Promise.race([
          promise,
          new Promise<never>((_, reject) => {
            signal.addEventListener('abort', () => reject(new Error('Fetch aborted')));
          }),
        ]);

        if (!signal.aborted) {
          dispatch({ type: 'FETCH_SUCCESS', payload: response });
          onSuccess?.(response);
        }
      } catch (error) {
        if (signal.aborted) return;
        dispatch({ type: 'FETCH_FAILURE', payload: error });
        onError?.(error);
      }
    },
    [onError, onSuccess],
  );

  return [state as FetcherState<T>, executeFetch];
}
